/**
 * WHITE LABEL CONFIGURATION
 *
 * Customize the appearance and behavior of the Customer Portal
 * without modifying component code.
 */

const brandingConfig = {
  // ==========================================
  // GENERAL BRANDING
  // ==========================================
  app: {
    name: 'HaliSoft',                    // Application name
    companyName: 'HaliSoft Solutions',   // Company name
    tagline: 'Risk Analysis Platform',   // Tagline
    logo: '📊',                          // Emoji or image URL
    supportEmail: 'support@halisoft.com',
    docsUrl: '/docs',
  },

  // ==========================================
  // SERVICE CONFIGURATION
  // ==========================================
  service: {
    name: 'Risk Analysis',               // Service name displayed
    description: 'Analyze your documents and make informed decisions',
    icon: '🎯',                          // Service icon

    // URL du service (can be overridden by REACT_APP_SERVICE_URL)
    // Default:
    // - production (Docker/nginx): same-origin proxy
    // - development: point to the real Halisoft-platform module (localhost:3000)
    url: process.env.REACT_APP_SERVICE_URL || (process.env.NODE_ENV === 'production'
      ? '/halilab/risk-analysis'
      : 'http://localhost:3000/halilab/risk-analysis'),

    // Open in new tab (true) or in page (false)
    // In dev we redirect to the real module instead of iframe-embedding it
    openInNewTab: process.env.NODE_ENV !== 'production',

    // Sandbox attributes for iframe
    sandbox: 'allow-same-origin allow-scripts allow-forms allow-popups allow-modals allow-downloads',
  },

  // ==========================================
  // QUOTA & ALERTES
  // ==========================================
  quota: {
    warningThreshold: 80,     // Orange alert from X%
    criticalThreshold: 95,    // Red alert from X%

    // Customizable messages
    messages: {
      warning: (remaining) => `Warning! You only have ${remaining} analyses left this month.`,
      critical: (remaining) => `Quota almost exhausted! Only ${remaining} analyses available.`,
      exceeded: 'You have used all your analyses this month.',
      blockedTitle: 'Service unavailable',
      blockedMessage: 'Your monthly quota is exhausted.',
      blockedHint: 'Upgrade to a higher plan to continue using the service.',
    },
  },

  // ==========================================
  // THEME & COLORS
  // ==========================================
  theme: {
    // Primary colors
    primaryColor: '#667eea',
    primaryGradientStart: '#667eea',
    primaryGradientEnd: '#764ba2',

    // Status colors
    successColor: '#48bb78',
    warningColor: '#f6ad55',
    criticalColor: '#e74c3c',

    // Text colors
    textPrimary: '#2d3748',
    textSecondary: '#718096',
    textLight: '#a0aec0',
  },

  // ==========================================
  // INTERFACE TEXTS
  // ==========================================
  texts: {
    // Navigation
    nav: {
      dashboard: 'Dashboard',
      subscription: 'Subscription',
      invoices: 'Invoices',
      settings: 'Settings',
      logout: 'Logout',
    },

    // Dashboard
    dashboard: {
      welcome: (name) => `Welcome, ${name}! 👋`,
      welcomeSubtitle: 'Manage your subscription and access your risk analyses',
      launchButton: 'Launch Risk Analysis',
      launchButtonDisabled: 'Quota Exceeded',
      launchHint: 'Access the service with your quota information',
    },

    // Page Risk Analysis
    riskAnalysis: {
      pageTitle: 'Risk Analysis',
      quotaLabel: 'analyses remaining this month',
      planLabel: 'Current Plan',
      managePlanButton: 'Manage Subscription',
      upgradeButton: 'Upgrade Plan',
      helpTitle: 'Need Help?',
      helpText: 'Contact our support team if you have any questions.',
      helpLink: 'Contact Support',
    },

    // Quota
    quota: {
      label: 'This Month\'s Quota',
      remaining: 'remaining',
      used: 'used',
      limit: 'limit',
    },

    // Plans
    plans: {
      free: 'FREE',
      starter: 'STARTER',
      pro: 'PRO',
    },
  },

  // ==========================================
  // FEATURES FLAGS
  // ==========================================
  features: {
    showInvoices: true,          // Afficher la section Invoices
    showSettings: true,          // Afficher la section Settings
    showQuotaInSidebar: true,    // Afficher le quota dans la sidebar
    showPlanInSidebar: true,     // Afficher le plan dans la sidebar
    showHelpCard: true,          // Afficher la carte d'aide
    allowDowngrade: true,        // Permettre le downgrade de plan
    allowCancellation: true,     // Allow subscription cancellation
  },
};

// ==========================================
// EXPORTS
// ==========================================

/**
 * Get a config value with path notation support
 * Exemple: getBrandingConfig('service.name') => 'Risk Analysis'
 */
export const getBrandingConfig = (path, defaultValue = null) => {
  const keys = path.split('.');
  let value = brandingConfig;

  for (const key of keys) {
    if (value && typeof value === 'object' && key in value) {
      value = value[key];
    } else {
      return defaultValue;
    }
  }

  return value !== undefined ? value : defaultValue;
};

/**
 * Get all configuration
 */
export const getAllBrandingConfig = () => brandingConfig;

/**
 * Get theme colors
 */
export const getTheme = () => brandingConfig.theme;

/**
 * Get interface texts
 */
export const getTexts = () => brandingConfig.texts;

/**
 * Get service configuration
 */
export const getServiceConfig = () => brandingConfig.service;

/**
 * Get app configuration
 */
export const getAppConfig = () => brandingConfig.app;

/**
 * Get quota configuration
 */
export const getQuotaConfig = () => brandingConfig.quota;

/**
 * Get feature flags
 */
export const getFeatures = () => brandingConfig.features;

export default brandingConfig;
